import {Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {SpinnerComponent} from "@shared/components/spinner/spinner.component";
import {BreadcrumbService} from "@cdss/services/breadcrumb.service";
import {PatientMeedRisksService} from "@shared/api/services/v2/patient-meed-risks.service";
import {PatientsService} from "@shared/api/services/v2/patients.service";
import {MeedRiskWeekly, RiskAssessmentLog} from "@shared/api/models/query/meed-risk.model";
import {Patient} from "@shared/api/models/query/patient.model";
import {ToastrService} from "ngx-toastr";
import {Pagination, PaginationResponse} from "@models/pagination.model";
import {EmptyError, firstValueFrom} from 'rxjs';
import {OverlaySpinnerComponent} from "@shared/components/overlay-spinner/overlay-spinner.component";
import {
    PatientDetailHeaderComponent
} from "@cdss/pages/patient-overview-v2/components/patient-detail-header/patient-detail-header.component";

interface DailyValue {
    P: number | string;
    C: number | string;
}

interface FactorDiff {
    value: number | string;
    color: 'up' | 'down' | 'stable';
}

interface FactorRisk {
    label: string;
    color: string;
    sub: string;
}

interface FactorTableData {
    name: string;
    unit: string;
    daily: DailyValue[];
    diff: FactorDiff;
    risk: FactorRisk;
}

const MS_PER_WEEK = 24 * 60 * 60 * 1000;

@Component({
    selector: 'cdss-patient-risk-summary',
    imports: [CommonModule, SpinnerComponent, OverlaySpinnerComponent, PatientDetailHeaderComponent],
    templateUrl: './patient-risk-summary.page.html',
    styleUrl: './patient-risk-summary.page.scss',
    standalone: true
})
export class PatientRiskSummaryPage implements OnInit {

    factorTableData: FactorTableData[] = [];
    loading = true;
    refreshing = true;
    patientId: number;
    patient: Patient | null = null;
    patientName: string = '';
    patientIdentifier: string = '';

    meedRisks: MeedRiskWeekly[] = [];
    currentMeedRisk: MeedRiskWeekly | null = null;
    assessmentLogs: RiskAssessmentLog[] = [];

    // Date navigation
    selectedDate: Date = new Date();
    dateRangeLabel = '';
    dates: string[] = [];

    // Action button states
    canTakeAction = true;

    get hasRiskRecord(): boolean {
        return this.currentMeedRisk?.id != null;
    }
    lastActionDate: string | null = null;
    lastActionType: string | null = null;

    showHistory = false;

    // Pagination for assessment logs
    logsPagination = new Pagination(10);
    logsTotalCount = 0;
    logsTotalPages = 0;
    logsCurrentPage = 1;

    readonly unknownRiskTooltip = 'Not enough data to assess MEED risk';

    isUnknownRisk(risk: string | null | undefined): boolean {
        return !risk || risk.toLowerCase() === 'unknown';
    }

    constructor(
        private activeRoute: ActivatedRoute,
        private router: Router,
        private breadcrumbService: BreadcrumbService,
        private patientMeedRisksService: PatientMeedRisksService,
        private patientsService: PatientsService,
        private toastr: ToastrService
    ) {
    }

    ngOnInit() {
        this.patientId = Number(this.activeRoute.snapshot.params['id']);

        this.breadcrumbService.set([
            {title: "Patient's Caseload", url: '/cdss/dashboard'},
            {title: "Patient Risk Summary", url: null},
        ]);

        this.updateDateRangeLabel();
        this.loadData();
    }

    async loadData() {
        this.refreshing = true;
        this.logsPagination.start = 0;

        try {
            // Load patient info
            this.patient = await this.patientsService.getById(this.patientId);
            this.updatePatientInfo();

            // Load MEED risk data
            const dateStr = this.selectedDate.toISOString().split('T')[0];
            this.meedRisks = await this.patientMeedRisksService.getMeedRisksWeekly(this.patientId, dateStr);
            this.currentMeedRisk = this.meedRisks[0];
            this.updateFactorTable();
            this.refreshAssessmentLogs()

        } catch (error) {
            console.error('Error loading data:', error);
            this.toastr.error('Error loading patient risk data');
            this.loading = false;
            this.refreshing = false;
        }
    }

    goBack(): void {
        this.router.navigate(['/cdss/dashboard']);
    }

    refreshAssessmentLogs() {
        this.patientMeedRisksService.getRiskAssessmentLogs(this.patientId, this.logsPagination).subscribe({
            next: (response: PaginationResponse<RiskAssessmentLog>) => {
                this.assessmentLogs = response.data || [];
                this.logsTotalCount = response.pagination?.totalCount ?? 0;
                this.logsTotalPages = response.pagination?.totalPages ?? 1;
                this.logsCurrentPage = response.pagination?.currentPage ?? 1;
                this.checkActionAvailability();
                this.loading = false;
                this.refreshing = false;
            },
            error: (error) => {
                console.error('Error loading assessment logs:', error);
                this.loading = false;
                this.refreshing = false;
            }
        });
    }

    logsNextPage() {
        if (this.logsCurrentPage < this.logsTotalPages) {
            this.logsPagination.start += 1;
            this.refreshAssessmentLogs();
        }
    }

    logsPrevPage() {
        if (this.logsCurrentPage > 1) {
            this.logsPagination.start -= 1;
            this.refreshAssessmentLogs();
        }
    }

    updatePatientInfo() {
        if (!this.patient) return;
        this.patientIdentifier = this.patient.nhsNumber || this.patient.id.toString();

        const userToDisplay = (this.patient.hasCarer && this.patient.carer?.user)
            ? this.patient.carer.user
            : this.patient.user;

        this.patientName = `${userToDisplay?.firstName ?? ''} ${userToDisplay?.lastName ?? ''}`.trim();
    }

    updateFactorTable() {
        if (!this.currentMeedRisk) {
            this.dates = []
            this.factorTableData = []
            return
        }
        const weeklyDetails = this.currentMeedRisk.factorWeeklyDetails || [];
        const date = Object.keys(this.currentMeedRisk.factorDailyDetails).sort();
        this.dates = date.map(d => new Date(d).toLocaleDateString('en-GB', {day: 'numeric', month: 'short'}).toUpperCase());

        this.factorTableData = weeklyDetails.map(entry => {
            const factorTableDataRow: FactorTableData = {
                name: entry.factorName,
                unit: '',
                daily: [],
                diff: {
                    value: entry.diff ?? '-',
                    color: (entry.diff !== undefined && entry.diff !== null)
                        ? (entry.diff < 0 ? 'down' : entry.diff > 0 ? 'up' : 'stable')
                        : 'stable'
                },
                risk: {
                    label: entry?.risk ? `${entry.risk} Risk` : 'Unknown Risk',
                    color: entry?.risk ? entry.risk.toLowerCase() : 'unknown',
                    sub: entry ? entry.riskTrigger || '' : ''
                }
            };

            date.forEach(key => {
                const dailyData = this.currentMeedRisk!.factorDailyDetails[key];
                if (!dailyData || dailyData.length === 0) {
                    factorTableDataRow.daily.push({ P: '-', C: '-' });
                } else {
                    const dayMatch = dailyData.find(d => d.factorName === entry.factorName);
                    factorTableDataRow.daily.push({
                        P: dayMatch?.patientValue ?? '-',
                        C: dayMatch?.carerValue ?? '-'
                    });
                }
            });

            return factorTableDataRow;
        });
    }

    checkActionAvailability() {
        const todayStr = new Date().toISOString().split('T')[0];

        const todayAction = this.assessmentLogs.find(log => {
            const logDate = new Date(log.createdAt).toISOString().split('T')[0];
            return logDate === todayStr &&
                (log.actionTaken === 'No action needed' || log.actionTaken === 'Risk assessment updated');
        });

        if (todayAction) {
            this.canTakeAction = false;
            this.lastActionDate = todayAction.createdAt;
            this.lastActionType = todayAction.actionTaken;
        } else {
            this.canTakeAction = true;
            this.lastActionDate = null;
            this.lastActionType = null;
        }
    }

    async onActionClick(actionTaken: "No action needed" | "Risk assessment updated") {
        if (!this.canTakeAction) {
            this.toastr.warning('You can only perform this action once per day');
            return;
        }

        const patientMeedRiskId = this.currentMeedRisk?.id;
        if (patientMeedRiskId == null) {
            // Never record an unlinked log: the action must reference the record on screen.
            return;
        }

        try {
            const result = await firstValueFrom(
                this.patientMeedRisksService.createRiskAssessmentLog(this.patientId, actionTaken, Number(patientMeedRiskId))
            );
            // The log interceptor already toasted the error and resolves 403/404 with the message string.
            if (typeof result === 'string') {
                return;
            }

            this.toastr.success(`Action "${actionTaken}" recorded successfully`);

            // Reload data to update logs and action availability
            this.loadData();

        } catch (error) {
            // The log interceptor already toasted the 403/404 and resolved it with a string, which
            // HttpClient drops from the body stream, so firstValueFrom() rejects with EmptyError.
            // Nothing is left to report: a second toast would only duplicate the interceptor's.
            if (error instanceof EmptyError) {
                return;
            }
            console.error('Error creating assessment log:', error);
            this.toastr.error(error?.error?.error ?? error?.error?.message ?? 'Error recording action');
        }
    }

    previousWeek() {
        this.selectedDate = new Date(this.selectedDate.getTime() - MS_PER_WEEK)
        this.updateDateRangeLabel();
        this.loadData();
    }

    nextWeek() {
        const today = new Date();
        if (this.selectedDate < today) {
            this.selectedDate = new Date(this.selectedDate.getTime() + MS_PER_WEEK)
            if (this.selectedDate > today) this.selectedDate = today;
            this.updateDateRangeLabel();
            this.loadData();
        }
    }

    updateDateRangeLabel() {
        const end = new Date(this.selectedDate);
        const start = new Date(this.selectedDate);
        // The MEED window for a date D is D-6..D, today included (UNIVAPLAT-2892).
        start.setDate(end.getDate() - 6);

        const options: Intl.DateTimeFormatOptions = {day: 'numeric', month: 'long'};
        const startStr = start.toLocaleDateString('en-GB', options);
        const endStr = end.toLocaleDateString('en-GB', options);

        this.dateRangeLabel = `${startStr}–${endStr}`;
    }
}
